import { Body } from '@hover/blueprint';
import { SimpleGrid } from '@hover/blueprint/chakra';
import React, { useContext } from 'react';

import { InspireMeCard } from 'src/modules/designStudio/components/DesignMenu/Menu/InspireMeCard';
import { MenuHeader } from 'src/modules/designStudio/components/DesignMenu/Menu/MenuHeader';
import { Mode, historyContext } from 'src/modules/designStudio/providers';
import { context } from 'src/modules/designStudio/providers/DesignStudioServiceProvider';
import { Button } from 'src/shared/components';
import { FeatureTracking } from 'src/shared/constants/FeatureFlag';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { Page, buttonPressed } from 'src/shared/telemetry';

export const InspireMeMenu = ({
  headerText,
  closeMenu,
}: {
  headerText: string | null;
  closeMenu: () => void;
}) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const {
    historyState,
    clearInspireMeImage,
    waitForInspireMeImage,
    setInspireMeImage,
  } = useContext(historyContext);
  const { appService } = useContext(context);
  const appSnapshot = appService.getSnapshot
    ? appService.getSnapshot()
    : {
        context: {
          orgId: undefined,
          partnerId: undefined,
          userId: undefined,
        },
      };

  const telemetryCommon = {
    primary_cta: false,
    job_id: historyState.jobId ?? undefined,
    org_id: appSnapshot.context.orgId,
    partner_id: appSnapshot.context.partnerId,
    user_id: appSnapshot.context.userId,
    user_email: historyState.email ?? undefined,
    page_or_screen_name: Page.INSPIRATION,
    design_studio_mode: Mode.DEFAULT,
    feature: FeatureTracking.INSPIRE_ME,
  };

  return (
    <>
      <MenuHeader
        closeMenu={() => {
          clearInspireMeImage();
          closeMenu();
        }}
        headerText={headerText!}
      />
      <Body fontSize={300} fontWeight={400}>
        {t('menu.inspireMe.subtitle')}
      </Body>
      <Button
        alignSelf="stretch"
        onClick={() => {
          buttonPressed({
            button_text: t('menu.inspireMe.button'),
            button_location: 'inspiration-panel',
            track_id: 'inspiration-generate-image',
            ...telemetryCommon,
          });
          waitForInspireMeImage();
        }}
        size="large"
        type="submit"
      >
        {t('menu.inspireMe.button')}
      </Button>
      <Body as="h2" marginBottom={400} size={400}>
        {historyState.inspireMePastImageURLs.length > 0
          ? t('menu.inspireMe.table')
          : ''}
      </Body>
      <SimpleGrid
        columns={{ base: 2, tablet: undefined }}
        display={{ base: 'flex', tablet: 'grid' }}
        flexShrink={1}
        gap={400}
        margin="-100"
        minChildWidth="120px"
        minHeight="80px"
        overflowX={{ base: 'auto', tablet: 'visible' }}
        overflowY={{ base: 'visible', tablet: 'auto' }}
        padding="100"
      >
        {historyState.inspireMePastImageURLs.map(gmlImage => (
          <InspireMeCard
            alignSelf="center"
            height={{ base: '100%', tablet: 'auto' }}
            imgPath={gmlImage}
            isSelected={false}
            justifySelf="center"
            key={gmlImage}
            maxWidth="160px"
            onSelected={() => {
              buttonPressed({
                button_text: '',
                button_location: 'inspiration-panel',
                track_id: 'inspiration-past-images',
                ...telemetryCommon,
              });
              setInspireMeImage(gmlImage);
            }}
            width="100%"
          />
        ))}
      </SimpleGrid>
    </>
  );
};
